import { z } from "zod";
import { ufSchema } from "./calculation.js";
import { environmentSchema } from "./context.js";
import { fiscalResearchTaskStateSchema } from "./fiscal-research.js";
/**
 * A coverage gap is one NCM (+ optional CEST) × origin → destination route for
 * which a calculation found no published fiscal rule. Gaps are operational
 * work items: they queue grounded research and record what a human found.
 * They never create, approve, publish or calculate a tax rule.
 */
export const coverageGapStatusSchema = z.enum([
    "OPEN",
    "RESEARCHING",
    "NO_RULE_FOUND",
    "RULE_FOUND",
    "RESOLVED",
    "DISMISSED",
]);
/**
 * What a reviewer concluded after research. `NO_ST_FOUND` is a valid,
 * evidence-backed answer ("checked, no ICMS-ST for this NCM and route as of
 * date"), not a failure; `INCONCLUSIVE` keeps the gap open for another pass.
 */
export const coverageGapFindingOutcomeSchema = z.enum([
    "NO_ST_FOUND",
    "RULE_FOUND",
    "INCONCLUSIVE",
]);
const ncmSchema = z.string().regex(/^\d{8}$/);
const cestSchema = z.string().regex(/^\d{2}\.\d{3}\.\d{2}$/);
export const coverageGapFindingSchema = z
    .object({
    outcome: coverageGapFindingOutcomeSchema,
    note: z.string().trim().min(10).max(2_000),
    evidenceIds: z.array(z.string().uuid()).max(10),
    checkedAsOf: z.string().date(),
    recordedBy: z.string().uuid(),
    recordedAt: z.string().datetime(),
})
    .strict();
export const coverageGapSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    environment: environmentSchema,
    ncm: ncmSchema,
    cest: cestSchema.nullable(),
    originUf: ufSchema,
    destinationUf: ufSchema,
    status: coverageGapStatusSchema,
    /** Calculation items that hit this gap. Drives research priority. */
    occurrenceCount: z.number().int().positive(),
    calculationCount: z.number().int().positive(),
    firstSeenAt: z.string().datetime(),
    lastSeenAt: z.string().datetime(),
    lastCalculationId: z.string().uuid(),
    lastCalculationDate: z.string().date(),
    researchTaskId: z.string().uuid().nullable(),
    researchTaskStatus: fiscalResearchTaskStateSchema.nullable(),
    finding: coverageGapFindingSchema.nullable(),
    updatedAt: z.string().datetime(),
    revision: z.number().int().positive(),
})
    .strict();
export const coverageGapPageSchema = z
    .object({
    items: z.array(coverageGapSchema).max(200),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
    totalEstimate: z.number().int().nonnegative(),
})
    .strict();
export const coverageGapResearchRequestSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    maxDocuments: z.number().int().min(1).max(10).default(5),
})
    .strict();
/** Queue research for the highest-priority open gaps, bounded per call. */
export const coverageGapBatchResearchRequestSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    limit: z.number().int().min(1).max(10).default(5),
    maxDocuments: z.number().int().min(1).max(10).default(5),
})
    .strict();
export const coverageGapBatchResearchResponseSchema = z
    .object({
    queued: z.array(coverageGapSchema).max(10),
    failed: z
        .array(z.object({ gapId: z.string().uuid(), code: z.string().min(1).max(80) }).strict())
        .max(10),
})
    .strict();
export const coverageGapFindingRequestSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    outcome: coverageGapFindingOutcomeSchema,
    note: z.string().trim().min(10).max(2_000),
    evidenceIds: z.array(z.string().uuid()).max(10).default([]),
    checkedAsOf: z.string().date(),
})
    .strict()
    .superRefine((value, context) => {
    if (new Set(value.evidenceIds).size !== value.evidenceIds.length) {
        context.addIssue({
            code: "custom",
            path: ["evidenceIds"],
            message: "evidenceIds must be unique",
        });
    }
    if (value.outcome !== "INCONCLUSIVE" && value.evidenceIds.length === 0) {
        context.addIssue({
            code: "custom",
            path: ["evidenceIds"],
            message: "A NO_ST_FOUND or RULE_FOUND finding must cite at least one evidence record",
        });
    }
});
export const coverageGapDismissRequestSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    reason: z.string().trim().min(10).max(1_000),
})
    .strict();
/**
 * Grounded research query for one gap, in the language of the sources. It
 * asks for the primary act and never for a rate to be assumed.
 */
export function coverageGapResearchQuery(gap) {
    const ncm = `${gap.ncm.slice(0, 4)}.${gap.ncm.slice(4, 6)}.${gap.ncm.slice(6, 8)}`;
    const route = gap.originUf === gap.destinationUf
        ? `operação interna em ${gap.destinationUf}`
        : `operação interestadual ${gap.originUf} para ${gap.destinationUf} (protocolo ou convênio ICMS)`;
    const cest = gap.cest ? ` CEST ${gap.cest}` : "";
    return `ICMS substituição tributária NCM ${ncm}${cest} ${route}: existe ST vigente em ${gap.checkedAsOf}? MVA original, alíquota interna e ato legal`.slice(0, 500);
}
//# sourceMappingURL=coverage-gaps.js.map